import { Vec2, makeDimension, type DimensionEntity, type DimensionKind } from '@lakaran/geometry';
import { Transaction } from '@lakaran/engine';
import { Tool, type ToolField } from './tool';

const MIN_LENGTH = 1e-9;

/** Shared pick-pick-place flow for linear and aligned dimensions. */
abstract class DimensionToolBase extends Tool {
  protected abstract readonly kind: DimensionKind;

  private first?: Vec2;
  private second?: Vec2;

  protected begin(): void {
    this.host.prompt(`${this.name}  Specify first extension line origin:`);
  }

  override get referencePoint(): Vec2 | undefined {
    return this.second ?? this.first;
  }

  point(p: Vec2): void {
    if (!this.first) {
      this.first = p;
      this.host.setReference(p);
      this.host.prompt(`${this.name}  Specify second extension line origin:`);
      return;
    }
    if (!this.second) {
      if (this.first.distanceTo(p) < MIN_LENGTH) return;
      this.second = p;
      this.host.setReference(undefined);
      this.host.prompt(`${this.name}  Specify dimension line location:`);
      return;
    }
    this.finish(this.offsetFor(p));
  }

  override hover(p: Vec2): void {
    if (!this.first || !this.second) return;
    const offset = this.offsetFor(p);
    if (Math.abs(offset) < 1e-6) {
      this.host.setDraft([]);
      return;
    }
    this.host.setDraft([this.build(offset)]);
  }

  override getFields(cursor: Vec2): ToolField[] | null {
    if (!this.first || !this.second) return null;
    return [
      { id: 'offset', label: 'Offset', unit: 'mm', value: this.offsetFor(cursor), decimals: 3 },
    ];
  }

  override applyFields(values: Record<string, number>): void {
    if (!this.first || !this.second) return;
    this.finish(values['offset'] ?? 0);
  }

  override cancel(): void {
    this.first = undefined;
    this.second = undefined;
    super.cancel();
  }

  private finish(offset: number): void {
    if (Math.abs(offset) < 1e-6) {
      this.host.editor.message(`${this.name}  Offset must be non-zero.`);
      return;
    }
    this.host.editor.commit(
      new Transaction().add(this.build(offset)),
      this.name,
    );
    this.host.endTool();
  }

  private offsetFor(p: Vec2): number {
    if (!this.first || !this.second) return 0;
    const base = this.second.sub(this.first);
    const dir =
      this.kind === 'aligned'
        ? base.normalize()
        : Math.abs(base.x) >= Math.abs(base.y)
          ? Vec2.of(1, 0)
          : Vec2.of(0, 1);
    return p.sub(this.first).dot(dir.perp());
  }

  private build(offset: number): DimensionEntity {
    const editor = this.host.editor;
    return makeDimension(this.first!, this.second!, offset, this.host.doc.currentLayerId, {
      kind: this.kind,
      textHeight: editor.settings.dimensionTextHeight,
      arrowSize: editor.settings.dimensionArrowSize,
      precision: editor.settings.lengthDecimals,
    });
  }
}

export class LinearDimensionTool extends DimensionToolBase {
  readonly name = 'DIM';
  readonly title = 'Linear dimension';
  readonly aliases = ['dim', 'dli', 'dimlinear'];
  protected readonly kind: DimensionKind = 'linear';
}

export class AlignedDimensionTool extends DimensionToolBase {
  readonly name = 'DIMALIGNED';
  readonly title = 'Aligned dimension';
  readonly aliases = ['dal', 'dimaligned', 'dimali'];
  protected readonly kind: DimensionKind = 'aligned';
}

export function createAnnotationTools(): Array<() => Tool> {
  return [() => new LinearDimensionTool(), () => new AlignedDimensionTool()];
}
